import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useButtonStore } from "../components/ButtonState";
import { FreeboardingParagraph } from "../components/FreeboardingParagraph";
import { FreeboardingTitle } from "../components/FreeboardingTitle";
import { OutlinedButton } from "../components/OutlinedButton";
import OutlinedCard from "../components/OutlinedCard";
import { TargetIcon } from "../components/TargetIcon";
import type { SelectionButtonProps } from "./SelectionButtonProps";

interface WeekDaySelectorProps {
  titleText: string;
  bodyText: string;
  numSelectionsRequired?: number;
  onSelectionChange: (selectedDays: SelectionButtonProps[]) => void; // Callback function with selected day names
  selectionText?: string;
}

// Define the weekdays for mobile and desktop. Do NOT combine to respect Selection Button Props type
const desktopWeekdays: SelectionButtonProps[] = [
  { id: "sun", label: "Su" },
  { id: "mon", label: "Mo" },
  { id: "tue", label: "Tu" },
  { id: "wed", label: "We" },
  { id: "thu", label: "Th" },
  { id: "fri", label: "Fr" },
  { id: "sat", label: "Sa" },
];

const mobileWeekdays: SelectionButtonProps[] = [
  { id: "sun", label: "Sunday" },
  { id: "mon", label: "Monday" },
  { id: "tue", label: "Tuesday" },
  { id: "wed", label: "Wednesday" },
  { id: "thu", label: "Thursday" },
  { id: "fri", label: "Friday" },
  { id: "sat", label: "Saturday" },
];

const WeekDaySelector = ({
  titleText,
  bodyText,
  numSelectionsRequired,
  selectionText,
  onSelectionChange,
}: WeekDaySelectorProps) => {
  const [selectedDays, setSelectedDays] = useState<SelectionButtonProps[]>([]);
  const setButtonEnabled = useButtonStore((state) => state.setButtonEnabled);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const WEEKDAYS = isMobile ? mobileWeekdays : desktopWeekdays;

  useEffect(() => {
    if (selectedDays.length === numSelectionsRequired) {
      setButtonEnabled(true);
    } else {
      setButtonEnabled(false);
    }

    onSelectionChange(selectedDays);
  }, [
    selectedDays,
    setButtonEnabled,
    numSelectionsRequired,
    onSelectionChange,
  ]);

  const handleDayClick = (day: SelectionButtonProps) => {
    if (selectedDays.includes(day)) {
      // Deselect the day
      setSelectedDays(
        selectedDays.filter((selectedDay) => selectedDay !== day),
      );
    } else {
      if (
        !numSelectionsRequired ||
        selectedDays.length < numSelectionsRequired
      ) {
        setSelectedDays([...selectedDays, day]);
      }
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      width="100%"
      height="100%"
      sx={{ overflowY: "auto", padding: 2 }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
      >
        <FreeboardingTitle>{titleText}</FreeboardingTitle>
        <FreeboardingParagraph>{bodyText}</FreeboardingParagraph>

        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={isMobile ? 2 : 1}
          marginTop={3}
          marginBottom={2}
          width="100%"
          justifyContent="center"
        >
          {WEEKDAYS.map((day) => (
            <OutlinedButton
              key={day.id}
              className={selectedDays.includes(day) ? "Mui-selected" : ""}
              onClick={() => {
                handleDayClick(day);
              }}
              fullWidth
              sx={{
                textAlign: "center",
                justifyContent: "center",
                "& .MuiButton-label": {
                  justifyContent: "center",
                  width: "100%",
                },
              }}
            >
              {day.label}
            </OutlinedButton>
          ))}
        </Stack>
      </Box>

      {selectedDays.length === numSelectionsRequired && selectionText && (
        <Box sx={{ marginTop: 2, marginBottom: 2 }}>
          <OutlinedCard
            text={selectionText}
            icon={<TargetIcon />}
            cardColor="success.light"
            borderColor="success.main"
          />
        </Box>
      )}
    </Box>
  );
};

export default WeekDaySelector;
